import { useState } from "react";
import { http } from "@/lib/api";
import { PixelEvents } from "@/lib/pixel";
import { toast } from "sonner";
import { Mail, ArrowRight, CheckCircle2 } from "lucide-react";

function anonId() {
    let id = localStorage.getItem("sf_anon");
    if (!id) {
        id = (crypto.randomUUID?.() || Math.random().toString(36).slice(2) + Date.now());
        localStorage.setItem("sf_anon", id);
    }
    return id;
}

export default function EmailCapture({ lang = "fr", source = "landing_footer" }) {
    const [email, setEmail] = useState("");
    const [done, setDone] = useState(false);
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email.includes("@")) { toast.error(lang === "fr" ? "Email invalide" : "Invalid email"); return; }
        setBusy(true);
        try {
            await http.post("/leads", { email, source, anon_id: anonId(), language: lang });
            PixelEvents.lead("email_capture");
            setDone(true);
            toast.success(lang === "fr" ? "Inscrit ✨" : "You're in ✨");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error");
        } finally { setBusy(false); }
    };

    return (
        <section id="join" data-testid="email-capture-section" className="relative py-24 border-t border-[#253046]">
            <div className="max-w-4xl mx-auto px-4 sm:px-8">
                <div className="relative rounded-2xl border border-[#FF4D00]/30 bg-gradient-to-br from-[#161D2A] via-[#0F141C] to-[#0B111B] p-8 sm:p-12 overflow-hidden">
                    <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#FF4D00]/10 blur-3xl" />
                    <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full bg-[#0099FF]/10 blur-3xl" />

                    <div className="relative">
                        <div className="overline">08 · Waitlist</div>
                        <h2 className="font-display text-3xl sm:text-4xl font-black uppercase mt-3 leading-tight">
                            {lang === "fr" ? "Raté le Founder Deal ?" : "Missed the Founder Deal?"}
                        </h2>
                        <p className="mt-4 text-slate-400 max-w-xl">
                            {lang === "fr"
                                ? "Laisse ton email — on t'envoie le prochain drop 48h avant tout le monde, plus 3 modèles de scripts virals gratuits."
                                : "Drop your email — get the next drop 48h before everyone else, plus 3 free viral script templates."}
                        </p>

                        {done ? (
                            <div data-testid="email-capture-success" className="mt-8 inline-flex items-center gap-3 px-5 py-3.5 rounded border border-[#10B981]/40 bg-[#10B981]/10">
                                <CheckCircle2 className="w-5 h-5 text-[#10B981]" />
                                <span className="text-sm text-slate-100 font-medium">
                                    {lang === "fr" ? "Tu es sur la liste. On te contacte très vite." : "You're on the list. We'll reach out soon."}
                                </span>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row gap-2 max-w-lg">
                                <div className="relative flex-1">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                                    <input
                                        data-testid="email-capture-input"
                                        type="email" required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="you@studio.io"
                                        className="w-full pl-10 pr-3 py-3 bg-[#080B10] border border-[#253046] rounded text-sm focus:outline-none focus:border-[#FF4D00]"
                                    />
                                </div>
                                <button
                                    data-testid="email-capture-submit"
                                    type="submit" disabled={busy}
                                    className="flame-gradient text-black font-semibold px-5 py-3 rounded flex items-center justify-center gap-2 kinetic flame-glow disabled:opacity-60 sm:w-48"
                                >
                                    {busy ? "···" : (<>{lang === "fr" ? "Rejoindre" : "Join"} <ArrowRight className="w-4 h-4" /></>)}
                                </button>
                            </form>
                        )}

                        <div className="mt-5 flex flex-wrap gap-6 text-[10px] font-mono-sf text-slate-500 uppercase tracking-widest">
                            <span>• {lang === "fr" ? "Zéro spam" : "Zero spam"}</span>
                            <span>• {lang === "fr" ? "Désinscription en 1 clic" : "One-click unsubscribe"}</span>
                            <span>• {lang === "fr" ? "3 emails / mois max" : "3 emails / month max"}</span>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
